import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Panel, Field, NSelect, Loading, Empty, Plate, DriverStatus, DemoTag, D_STATUS, useFetch } from "@/components/common";
import { DemoCleanupButton, ConfirmDialog } from "@/components/DemoCleanup";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg, fDate, fNum, todayIso } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTS = Object.entries(D_STATUS).map(([k, x]) => ({ value: k, label: x[0] }));

function DriverDialog({ d, onClose, onSaved }) {
  const [f, setF] = useState({ first_name: "", last_name: "", status: "actif", ...d });
  const set = (k) => (x) => setF({ ...f, [k]: x?.target ? x.target.value : x });
  const save = async () => {
    try {
      const body = { first_name: f.first_name, last_name: f.last_name, status: f.status };
      if (d.id) await api.put(`/drivers/${d.id}`, body);
      else await api.post("/drivers", body);
      toast.success("Chauffeur enregistré");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="driver-dialog">
        <DialogHeader><DialogTitle className="font-display text-[#0E2D43]">{d.id ? "Modifier le chauffeur" : "Nouveau chauffeur"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Prénom"><Input value={f.first_name} onChange={set("first_name")} data-testid="driver-first-name-input" /></Field>
          <Field label="Nom"><Input value={f.last_name} onChange={set("last_name")} data-testid="driver-last-name-input" /></Field>
          <Field label="Statut"><NSelect value={f.status} onChange={set("status")} options={STATUS_OPTS} testid="driver-status-select" /></Field>
        </div>
        <p className="text-xs text-slate-500">Un chauffeur inactif ou archivé n'apparaît plus dans le formulaire de contrôle ; son historique est conservé.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="driver-save-button">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsageLookup({ version }) {
  const [date, setDate] = useState(todayIso());
  const [del, setDel] = useState(null);
  const { data, loading, reload } = useFetch("/drivers/usage", { date }, [version]);
  const remove = async () => {
    try {
      await api.delete(`/checks/${del.id}/demo`);
      toast.success("Contrôle de démonstration supprimé");
      setDel(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Qui utilisait quel véhicule ?" className="mb-6" testid="driver-usage-lookup"
      action={<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44 bg-white" data-testid="usage-date-input" />}>
      {loading ? <Loading /> : !data?.length ? <Empty text={`Aucun contrôle le ${fDate(date)}`} /> : (
        <div className="overflow-x-auto"><table className="tbl w-full" data-testid="usage-table">
          <thead><tr><th>Véhicule</th><th>Chauffeur attribué</th><th>Contrôlé par</th><th className="text-right">Km</th><th></th></tr></thead>
          <tbody>{data.map((c) => (
            <tr key={c.id} data-testid={`usage-row-${c.id}`}><td><div className="flex items-center gap-1.5"><Link to={`/livraison/vehicules/${c.vehicle_id}`}><Plate>{c.plate}</Plate></Link><DemoTag show={c.is_demo} /></div></td>
              <td className="font-semibold">{c.assigned_driver_id ? <Link to={`/livraison/chauffeurs/${c.assigned_driver_id}`} className="hover:underline">{c.driver_name}</Link> : c.driver_name}</td>
              <td className="text-slate-500">{c.controller_name}</td><td className="text-right tabular-nums">{fNum(c.mileage)}</td>
              <td>{c.is_demo && <button onClick={() => setDel(c)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Supprimer ce contrôle de démonstration" data-testid={`usage-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>}</td></tr>
          ))}</tbody>
        </table></div>
      )}
      {del && (
        <ConfirmDialog title="Supprimer ce contrôle de démonstration ?" onConfirm={remove} onClose={() => setDel(null)} testid="usage-delete-dialog">
          <p>{del.plate} — {fDate(del.date)} · contrôlé par {del.controller_name}. Le contrôle et ses anomalies fictives seront supprimés définitivement.</p>
        </ConfirmDialog>
      )}
    </Panel>
  );
}

export default function Chauffeurs() {
  const [status, setStatus] = useState("");
  const [edit, setEdit] = useState(null);
  const [version, setVersion] = useState(0);
  const { data, loading, reload } = useFetch("/drivers", { status });
  const { canEdit } = useAuth().user;
  return (
    <div data-testid="drivers-page">
      <PageHeader overline="Véhicules de livraison" title="Chauffeurs">
        <div className="w-44"><NSelect value={status} onChange={setStatus} testid="drivers-filter-status"
          options={[{ value: "", label: "Actifs et inactifs" }, ...STATUS_OPTS, { value: "tous", label: "Tous" }]} /></div>
        {canEdit && <DemoCleanupButton onDone={() => setVersion((x) => x + 1)} />}
        {canEdit && <Button onClick={() => setEdit({})} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="driver-create-button"><Plus className="w-4 h-4 mr-1" /> Nouveau chauffeur</Button>}
      </PageHeader>
      <UsageLookup version={version} />
      {loading ? <Loading /> : !data?.length ? <Empty text="Aucun chauffeur — ajoutez-en ou importez-les (Paramètres → Imports)" /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto rise">
          <table className="tbl w-full" data-testid="drivers-table">
            <thead><tr><th>Nom</th><th>Prénom</th><th>Statut</th><th>Dernier véhicule</th><th>Dernière utilisation</th><th></th></tr></thead>
            <tbody>{data.map((d) => (
              <tr key={d.id} data-testid={`driver-row-${d.id}`}>
                <td className="font-semibold"><Link to={`/livraison/chauffeurs/${d.id}`} className="text-[#0E2D43] hover:underline" data-testid={`driver-link-${d.id}`}>{d.last_name}</Link></td>
                <td>{d.first_name}</td><td><DriverStatus v={d.status} /></td>
                <td>{d.last_plate ? <Plate>{d.last_plate}</Plate> : "—"}</td><td>{fDate(d.last_date)}</td>
                <td>{canEdit && <button onClick={() => setEdit(d)} className="p-1.5 rounded hover:bg-slate-100" title="Modifier" data-testid={`driver-edit-${d.id}`}><Pencil className="w-4 h-4" /></button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {edit && <DriverDialog d={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
